import { JSX } from 'preact';
import { SubsectionDragProvider } from 'client/courses/components/page-builder/subsection-drag-provider';
import {
  SubsectionWrapper,
  SubsectionEditor,
} from 'client/courses/components/page-builder/subsection-toolbar';
import { BtnAdd } from 'client/courses/components/page-builder/btn-add';
import { CardGrid } from 'client/courses/components/page-builder/card-grid';
import {
  getCardClasses,
  getCardStyle,
} from 'client/courses/components/page-builder/card-style-utils';
import { CardStyles } from 'server/types/public-overrides';

export type CardLayoutItem = {
  id: string;
  [key: string]: any;
};

export type CardLayoutState = {
  cards: CardLayoutItem[];
  [key: string]: any;
};

export type CardLayoutBlockProps<
  T extends CardLayoutItem,
  S extends Record<string, any> = CardLayoutState,
> = {
  blockType: string;
  itemType: string;
  state: NonNullable<S>;
  setState: (updater: S | ((prev: S) => S)) => void;
  emptyCard: Omit<T, 'id'>;
  renderCard: (opts: {
    item: T;
    index: number;
    state: S;
    setState: CardLayoutBlockProps<T, S>['setState'];
    readonly: boolean;
    isBgLight: boolean;
  }) => JSX.Element;
  readonly?: boolean;
  isBgLight?: boolean;
  isSelected?: boolean;
  cardStyles?: CardStyles;
};

type RenderCardOpts<T extends CardLayoutItem, S extends Record<string, any> = CardLayoutState> = {
  item: T;
  index: number;
  cardStyles: CardStyles;
  renderCard: CardLayoutBlockProps<T, S>['renderCard'];
  state: S;
  setState: CardLayoutBlockProps<T, S>['setState'];
  readonly: boolean;
  isBgLight: boolean;
};

function renderSingleCard<
  T extends CardLayoutItem,
  S extends Record<string, any> = CardLayoutState,
>(opts: RenderCardOpts<T, S>): JSX.Element {
  const { item, index, cardStyles, renderCard, state, setState, readonly, isBgLight } = opts;
  return (
    <div class={getCardClasses(cardStyles)} style={getCardStyle(cardStyles)}>
      {renderCard({ item, index, state, setState, readonly, isBgLight })}
    </div>
  );
}

export function CardLayoutBlock<
  T extends CardLayoutItem,
  S extends Record<string, any> = CardLayoutState,
>(props: CardLayoutBlockProps<T, S>): JSX.Element {
  const {
    blockType,
    itemType,
    state,
    setState,
    emptyCard,
    renderCard,
    readonly = false,
    isBgLight = false,
    isSelected = false,
    cardStyles = {} as CardStyles,
  } = props;

  const items = ((state as any).cards || []) as T[];

  const handleReorder = (reorder: (items: T[]) => T[]) => {
    setState((s) => ({
      ...s,
      cards: reorder(s.cards as T[]),
    }));
  };

  const handleAdd = () => {
    setState((s) => ({
      ...s,
      cards: [
        ...s.cards,
        {
          ...emptyCard,
          id: crypto.randomUUID(),
        } as T,
      ],
    }));
  };

  const handleDelete = (id: string) => {
    setState((s) => ({
      ...s,
      cards: ((s as any).cards as CardLayoutItem[]).filter((x) => x.id !== id),
    }));
  };

  return (
    <SubsectionDragProvider table={blockType} onReorder={handleReorder}>
      <SubsectionWrapper>
        <CardGrid>
          {items.map((item, index) => (
            <SubsectionEditor
              key={item.id}
              id={item.id}
              disabled={readonly}
              table={blockType}
              onDelete={() => handleDelete(item.id)}
            >
              {renderSingleCard({
                item,
                index,
                cardStyles,
                renderCard,
                state,
                setState,
                readonly,
                isBgLight,
              })}
            </SubsectionEditor>
          ))}
        </CardGrid>

        {!readonly && isSelected && (
          <footer class="flex items-center justify-center w-full @lg:mt-8 @sm:mt-6 mt-4">
            <BtnAdd isBgLight={isBgLight} onClick={handleAdd}>
              + Add {itemType}
            </BtnAdd>
          </footer>
        )}
      </SubsectionWrapper>
    </SubsectionDragProvider>
  );
}
